/**
 * Turns BLE ping readings into eACH and source-strength figures.
 *
 * The `eachFit` module holds the maths and knows nothing about this product; this
 * module holds the decisions that are specific to the Duet's data: which value
 * keys carry each quantity, what counts as a missing reading, what background
 * concentration to assume, and how to express an absolute emission rate.
 */

import {
  assessFit,
  binSamples,
  CO2_PER_PERSON_LPH,
  emissionStrength,
  EmissionStrength,
  ExpFit,
  FitGates,
  FitRejection,
  fitExponential,
  Sample
} from './eachFit';

/** One quantity an eACH can be computed for. */
export type EachTarget = {
  /** Stable id, used for storage keys and React keys. */
  id: 'co2' | 'pn03' | 'pm25';
  /** BLE value keys that can supply it, in preference order. */
  keys: string[];
  label: string;
  unit: string;
  /**
   * Outdoor concentration assumed when none is given. The emission strength is
   * measured RELATIVE to this, so it matters: for CO₂ the global background is
   * well known and nearly constant, which is exactly why CO₂ is the standard
   * tracer. For particles there is no safe default — outdoor PM varies by
   * orders of magnitude — so 0 is used, which makes the figure "generation
   * needed to hold this plateau against clean outdoor air" and an
   * overestimate whenever outdoor air is not clean.
   */
  defaultBackground: number;
  /**
   * Whether a reading of exactly 0 means "no sensor" rather than "none
   * measured".
   *
   * For CO₂ it does: the atmosphere is never below ~420 ppm, so a 0 is a
   * sensor that is absent or not yet warmed up — and every BLE ping in
   * production currently reports co2_ppm = 0 for that reason. For PM and
   * particle counts a 0 is a real reading of clean air and must be kept, or
   * the fit silently drops the cleanest part of a decay and overstates the
   * plateau.
   */
  zeroMeansMissing: boolean;
  /** Volume-normalised strength and a room volume into an absolute labelled rate. */
  absolute: (perVolume: number, volumeM3: number) => { value: number; unit: string };
};

/** The three quantities the request asks for. */
export const EACH_TARGETS: readonly EachTarget[] = [
  {
    id: 'co2',
    keys: ['co2_ppm', 'co2'],
    label: 'CO₂',
    unit: 'ppm',
    defaultBackground: 420,
    zeroMeansMissing: true,
    /**
     * Converts ppm/h over m³ into litres of CO₂ per hour, since 1 ppm·m³ is
     * 1e-6 m³, which is 1e-3 L.
     * @param perVolume - Strength per unit volume, ppm/h.
     * @param volumeM3 - Room volume, m³.
     * @returns The absolute generation rate in litres per hour.
     */
    absolute: (perVolume, volumeM3) => ({ value: perVolume * volumeM3 * 1e-3, unit: 'L/h' })
  },
  {
    id: 'pn03',
    keys: ['pn03_m', 'pn03_b', 'pn03_t'],
    label: 'PN0.3',
    unit: '#/cm³',
    defaultBackground: 0,
    zeroMeansMissing: false,
    /**
     * #/cm³/h over m³ into particles per hour: 1 m³ = 1e6 cm³.
     * @param perVolume - Strength per unit volume, #/cm³/h.
     * @param volumeM3 - Room volume, m³.
     * @returns The absolute generation rate in particles per hour.
     */
    absolute: (perVolume, volumeM3) => ({ value: perVolume * volumeM3 * 1e6, unit: '#/h' })
  },
  {
    id: 'pm25',
    keys: ['pm25_m', 'pm2_5', 'pm25_b', 'pm25_t'],
    label: 'PM2.5',
    unit: 'µg/m³',
    defaultBackground: 0,
    zeroMeansMissing: false,
    /**
     * Converts µg/m³/h over m³ into µg per hour.
     * @param perVolume - Strength per unit volume, µg/m³/h.
     * @param volumeM3 - Room volume, m³.
     * @returns The absolute generation rate in µg per hour.
     */
    absolute: (perVolume, volumeM3) => ({ value: perVolume * volumeM3, unit: 'µg/h' })
  }
];

/** One row of the ventilation card. */
export type EachRow = {
  target: EachTarget;
  /** The value key the readings came from, or null when none carried data. */
  sourceKey: string | null;
  fit: ExpFit | null;
  /** Null when the fit may be reported as an eACH, otherwise why not. */
  rejection: FitRejection | null;
  emission: EmissionStrength | null;
  /** Occupancy implied by a CO₂ generation rate; null for everything else. */
  persons: number | null;
  background: number;
  /** Raw readings considered, before binning — shown so the gate is explicable. */
  rawCount: number;
};

/** Readings for one BLE value key, newest last. */
export type KeyedSamples = Record<string, Sample[]>;

/**
 * Bin width for the fit.
 *
 * Fifteen seconds over a five-minute window gives twenty points: enough to
 * constrain three parameters comfortably, coarse enough that each bin averages
 * ~15 pings at 1 Hz and the residuals are close to independent. Longer windows
 * scale the bin up so the point count stays in the same range rather than
 * growing into the thousands.
 * @param windowMs - Fitting window length, milliseconds.
 * @returns The bin width in milliseconds.
 */
export function binWidthFor(windowMs: number): number {
  return Math.max(5000, Math.round(windowMs / 20));
}

/**
 * Picks the first value key that actually carried usable readings.
 *
 * Preference order matters: the Duet reports the same quantity at three
 * heights and `_m` (mid) is the one the dashboard's thresholds are written
 * for, so it is tried first and a fallback is only used when mid is silent.
 * @param target - The quantity being computed.
 * @param samples - Readings keyed by BLE value key.
 * @returns The chosen key, or null when none carried usable readings.
 */
function pickKey(target: EachTarget, samples: KeyedSamples): string | null {
  const found = target.keys.find((key) => {
    const rows = samples[key];
    if (!rows || rows.length === 0) return false;
    return target.zeroMeansMissing ? rows.some((s) => s.y !== 0) : true;
  });
  return found ?? null;
}

/**
 * Fits one quantity over a window and derives its eACH and source strength.
 * @param target - The quantity to compute.
 * @param samples - Raw readings keyed by BLE value key.
 * @param fromMs - Window start, unix milliseconds.
 * @param toMs - Window end, unix milliseconds.
 * @param options - Background, room volume and gate overrides.
 * @param options.background - Outdoor concentration; the target's default when omitted.
 * @param options.volumeM3 - Room volume in m³, or null to skip absolute rates.
 * @param options.gates - Gate overrides; the module defaults when omitted.
 * @returns The row, always — a row that could not be fitted carries its reason.
 */
export function computeEachRow(
  target: EachTarget,
  samples: KeyedSamples,
  fromMs: number,
  toMs: number,
  options: {
    background?: number;
    volumeM3?: number | null;
    gates?: FitGates;
  } = {}
): EachRow {
  const background = options.background ?? target.defaultBackground;
  const volumeM3 = options.volumeM3 ?? null;
  const sourceKey = pickKey(target, samples);
  const spanHours = (toMs - fromMs) / 3600000;

  if (!sourceKey) {
    return {
      target,
      sourceKey: null,
      fit: null,
      rejection: 'no-data',
      emission: null,
      persons: null,
      background,
      rawCount: 0
    };
  }

  const raw = (samples[sourceKey] ?? []).filter(
    (s) => Number.isFinite(s.y) && !(target.zeroMeansMissing && s.y === 0)
  );
  const fit = fitExponential(binSamples(raw, fromMs, toMs, binWidthFor(toMs - fromMs)));
  const rejection = assessFit(fit, spanHours, options.gates);
  // Emission strength is only meaningful once the rate behind it is, since it
  // is lambda times a concentration difference. Showing one without the other
  // would present a number built on a rate we have just declined to report.
  const emission = rejection === null ? emissionStrength(fit, background, volumeM3, target.absolute) : null;
  const persons =
    target.id === 'co2' && emission?.absolute != null
      ? emission.absolute / CO2_PER_PERSON_LPH
      : null;

  return { target, sourceKey, fit, rejection, emission, persons, background, rawCount: raw.length };
}

/** Metres per degree of latitude — good to ~0.5% anywhere. */
const METRES_PER_DEG = 111320;

/**
 * How far the phone roamed inside the window, in metres, or null without GPS.
 *
 * This is a validity check, not decoration. The single-zone model assumes the
 * sensor sits in one well-mixed room; a sensor carried from a corridor into a
 * meeting room records a concentration change that fits an exponential
 * beautifully and has nothing to do with that room's ventilation. Only about
 * one BLE ping in eleven carries a fix, so this often cannot be checked at all
 * — which is reported as "unknown" rather than as "stationary".
 * @param points - Positions in the window.
 * @returns The largest separation between any point and the first, in metres.
 */
export function roamRadiusMetres(
  points: { latitude: number | null; longitude: number | null }[]
): number | null {
  const fixes = points.filter(
    (p) =>
      typeof p.latitude === 'number' &&
      Number.isFinite(p.latitude) &&
      typeof p.longitude === 'number' &&
      Number.isFinite(p.longitude)
  ) as { latitude: number; longitude: number }[];
  if (fixes.length < 2) return null;
  const base = fixes[0];
  const cos = Math.cos((base.latitude * Math.PI) / 180);
  let worst = 0;
  fixes.forEach((p) => {
    const dy = (p.latitude - base.latitude) * METRES_PER_DEG;
    const dx = (p.longitude - base.longitude) * METRES_PER_DEG * cos;
    worst = Math.max(worst, Math.sqrt(dx * dx + dy * dy));
  });
  return worst;
}

/**
 * Above this, treat the sensor as having moved between spaces and warn.
 *
 * Twenty-five metres is comfortably larger than consumer GPS scatter while
 * sitting still (typically 5-15 m indoors) and smaller than a walk between
 * rooms.
 */
export const ROAM_WARN_METRES = 25;
